/**
 * Static content for the Demographics channel detail page
 * (`/connect/demographics`). Schema fields, sample rows, coverage, and
 * freshness are quoted from the Data Catalog's own Demographics entry
 * (`src/@studio-ai/components/data-catalogue/signalMetadata.ts`) — copied
 * rather than imported, since that module backs an @mfour.com-gated tool and
 * this page is visible to every Home user. Action prompts are quoted from
 * `EXPLORE_CHANNELS`'s demographic entry (`src/@nav/explore-channels.ts`),
 * the same commands this channel's hover flyout used before it moved here.
 */

import type { ConnectChannel } from '../types';

export const DEMOGRAPHICS_CHANNEL: ConnectChannel = {
    name: 'Demographics',
    icon: 'user-circle-2',
    color: '#2E6649',
    coverage: 'US nationwide',
    freshness: 'Daily',
    since: '2016',
    exploreDaniPrompt: 'What can Demographics data tell me about my consumers?',
    actions: [
        {
            id: 'demographics-build',
            name: 'Build audiences',
            icon: 'users',
            summary:
                "who's in the audience you describe, and how many verified consumers it covers",
            template:
                'Build an audience of {audience|parents who buy QSR weekly} in {markets|the top 20 DMAs}',
        },
        {
            id: 'demographics-feasibility',
            name: 'Check feasibility',
            icon: 'compass',
            summary: 'how many verified consumers match your target',
            template:
                'How many verified consumers match {audience|my target definition} in {markets|all US markets}?',
        },
        {
            id: 'demographics-census',
            name: 'Weight Census',
            icon: 'scale',
            summary: 'how to weight your study to Census, with the scheme written out',
            template: 'Weight {survey|my last study} to Census and document the scheme',
            ask: { survey: 'Which study should DANI weight to Census?' },
        },
        {
            id: 'demographics-profile',
            name: 'Profile segments',
            icon: 'id-card',
            summary: 'who your audience is: age, income, geography and household',
            template: 'Profile {audience|my heavy buyers}: age, income, geography, household',
        },
    ],
    schema: [
        { field: 'consumer_id', type: 'string', description: 'Pseudonymous panel ID' },
        { field: 'age_range', type: 'string', description: 'Age bracket' },
        { field: 'gender', type: 'string', description: 'Gender' },
        { field: 'ethnicity', type: 'string', description: 'Ethnicity group' },
        { field: 'income', type: 'string', description: 'Income bracket' },
        { field: 'region', type: 'string', description: 'US region' },
        { field: 'education', type: 'string', description: 'Education level' },
        { field: 'marital_status', type: 'string', description: 'Marital status' },
        { field: 'state', type: 'string', description: '2-letter state code' },
        { field: 'dma', type: 'string', description: 'Designated Market Area' },
    ],
    sampleCols: ['Age Range', 'Gender', 'Ethnicity', 'Income', 'Region', 'Education', 'State'],
    sampleRows: [
        ['25-34', 'Male', 'White', '$50k-$75k', 'South', "Bachelor's", 'TX'],
        ['35-44', 'Female', 'Hispanic', '$75k-$100k', 'West', 'Some College', 'CA'],
        ['18-24', 'Female', 'Black', '$25k-$50k', 'Northeast', 'High School', 'NY'],
        ['45-54', 'Male', 'Asian', '$100k+', 'Midwest', "Master's", 'IL'],
        ['55-64', 'Female', 'White', '$50k-$75k', 'South', "Bachelor's", 'FL'],
    ],
};
